import type { ComponentType } from 'react'
import * as React from 'react'
import { Link, useLocation } from 'react-router-dom'

import { Box, Icon, List, Text, useToggle } from '@factorialco/deprecated-design-system'
import DOMPurify from 'dompurify'

import { observer } from 'mobx-react'

import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import type { AvatarUser, InboxRequest } from 'modules/notifications/hooks/useNotificationsRequests'
import { trackNotificationEvent } from 'modules/notifications/lib/TrackNotificationEvent'
import { dueDateStateData } from 'modules/notifications/lib/dueStateData'

import { Badge } from './Components/Badge'
import { DueStateBadge } from './Components/DueStateBadge'

export interface RequestComponentProps {
  request: InboxRequest
  index: number
  compact: boolean
  // Remove the request with the passed 'id' from the list if
  // the passed 'id' is found
  removeRequestById: (id: string) => void
}

export type RequestComponent = ComponentType<RequestComponentProps>

interface Props {
  request: InboxRequest
  index: number
  icon?: React.ComponentProps<typeof Icon.Medium>['icon']
  url?: string
  remoteUrl?: string
  compact?: boolean
  buttons?: React.ReactNode
  collapseContent?: React.ReactNode
  onOpen?: () => void
  avatarUsersMap?: Map<string, AvatarUser>
}

export const BaseRequestItem = observer(
  ({
    request,
    index,
    icon,
    url,
    remoteUrl,
    compact,
    buttons,
    collapseContent,
    onOpen,
    avatarUsersMap,
  }: Props) => {
    const [toggle, setToggle] = useToggle(false)
    const { text, htmlText, description, resourceCreatedAt, dueDate, categoryTitle } = request
    const { pathname } = useLocation()

    const formattedDueDate = dueDate ? stringToDate(dueDate, 'backend_datetime') : undefined

    const { state, dueTextColor, dueIconColor, dueIcon, dueDaysBetweenToday } =
      dueDateStateData(formattedDueDate)

    const createdAt = stringToDate(resourceCreatedAt, 'backend_datetime')

    const canSeeUrlButton = compact || (!compact && !collapseContent)

    const canSeeExtraContent = collapseContent && !compact

    const collapsableAction = () => {
      if (canSeeExtraContent) {
        onOpen && !toggle && onOpen()
        setToggle(!toggle)()
      }
    }

    const rowOnClick = (event: MouseEvent) => {
      event.stopPropagation()

      collapsableAction()
    }

    const titleFragment = () => {
      if (!htmlText) return <List.Title>{text}</List.Title>

      return (
        <Text size="200" color="grey1000" weight="normal" overflowWrap>
          <div
            className={'RequestItem_htmlTitle'}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(htmlText) }}
          />
        </Text>
      )
    }

    const body = (
      <Box gap="s8" minWidth="s0">
        {titleFragment()}

        {formattedDueDate && (
          <Box flexDirection="row" gap="s8" alignItems="center">
            <DueStateBadge state={state} days={dueDaysBetweenToday} />
            <Text size="200" color={dueTextColor}>
              <Box flexDirection="row" alignItems="center" gap="s4">
                <Icon.Medium icon={dueIcon} color={dueIconColor} />
                {i18n.t('dashboard.todo.due', { date: formattedDueDate.format('ll') })}
              </Box>
            </Text>
          </Box>
        )}

        <Text size="200">
          {createdAt.format('ll')} · {categoryTitle}
        </Text>

        {description && <List.Description ellipsis>{description}</List.Description>}
      </Box>
    )

    const dashboardCompactBody = (
      <Box>
        <Box paddingRight="s16">
          {formattedDueDate && (
            <Box flexDirection="row" gap="s8" alignItems="center">
              <Text size="200" color={dueTextColor}>
                {i18n.t('dashboard.todo.due', { date: dateToString(formattedDueDate, 'ui') })}
              </Text>

              {formattedDueDate && (
                <Box>
                  <DueStateBadge state={state} days={dueDaysBetweenToday} />
                </Box>
              )}
            </Box>
          )}
          <Text size="200" color="grey1000">
            {titleFragment()}
          </Text>

          {description && (
            <Box paddingY="s4">
              <List.Description ellipsis>{description}</List.Description>
            </Box>
          )}
        </Box>
      </Box>
    )

    const ActionWrapper = ({ children }: React.PropsWithChildren) => {
      if (canSeeUrlButton && url) {
        return (
          <Link
            to={url}
            data-testid="url-button"
            state={{ backTo: pathname }}
            onClick={() => {
              trackNotificationEvent(request, index, compact)
            }}
          >
            {children}
          </Link>
        )
      }

      if (canSeeUrlButton && remoteUrl) {
        return (
          <a
            data-testid="url-button"
            href={remoteUrl}
            onClick={() => {
              trackNotificationEvent(request, index, compact)
            }}
          >
            {children}
          </a>
        )
      }

      return (
        <Box onClick={rowOnClick} cursor="pointer">
          {children}
        </Box>
      )
    }

    const ActionButtons = () => {
      return (
        <Box
          onClick={(event) => {
            event.stopPropagation()
            event.preventDefault()
          }}
        >
          {buttons}
        </Box>
      )
    }

    const isOnDashboard = pathname.includes('dashboard')

    const ListItem = ({ children }: React.PropsWithChildren) =>
      isOnDashboard ? (
        <Box
          component="li"
          paddingX="s16"
          paddingY="s12"
          flexDirection="row"
          alignItems="center"
          justifyContent="spaceBetween"
        >
          {children}
        </Box>
      ) : (
        <List.Item>{children}</List.Item>
      )

    return (
      <>
        <Box className={'RequestItem_hover'}>
          <ActionWrapper>
            <ListItem>
              <Box
                flexDirection="row"
                justifyContent="spaceBetween"
                alignItems="flexStart"
                gap="s16"
                width="full"
              >
                <Box
                  flexDirection={compact ? 'column' : 'row'}
                  alignItems="flexStart"
                  justifyContent="spaceBetween"
                  width="full"
                >
                  {/* Icon */}
                  <Box
                    flexDirection="row"
                    gap={isOnDashboard ? 's16' : 's24'}
                    alignItems={isOnDashboard ? 'center' : 'flexStart'}
                    minWidth="s0"
                  >
                    <Box
                      flexDirection="row"
                      justifyContent="spaceBetween"
                      alignItems="center"
                      alignSelf="flexStart"
                    >
                      <Badge request={request} icon={icon} avatarUsersMap={avatarUsersMap} />
                    </Box>
                    {/* Content */}
                    {compact ? dashboardCompactBody : body}
                  </Box>

                  {/* Action */}
                  <Box flexDirection="row" gap="s16" alignItems="flexStart">
                    {/* Injected action buttons */}
                    {buttons && (
                      <Box paddingTop={compact ? 's8' : 's0'} paddingLeft={compact ? 's48' : 's0'}>
                        <ActionButtons />
                      </Box>
                    )}
                  </Box>
                </Box>

                {canSeeExtraContent && (
                  <>
                    {toggle ? (
                      <Icon.Medium icon="chevron-up" color="grey1000" />
                    ) : (
                      <Icon.Medium icon="chevron-down" color="grey1000" />
                    )}
                  </>
                )}
              </Box>
            </ListItem>
          </ActionWrapper>
        </Box>
        {toggle && (
          <Box
            border={{ top: { width: 's1', color: 'grey400', style: 'solid' } }}
            onClick={(event) => event.stopPropagation()}
          >
            <List.Item>
              <Box padding="s32" width="full">
                {collapseContent}
              </Box>
            </List.Item>
          </Box>
        )}
      </>
    )
  }
)
